import { Center, Stack, Text, Title } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { IconFigmaLock } from '@/assets/icons';
import { FormErrorMessage, MutationButton, PasswordField } from '@/components/common';
import { AuthCard } from '@/features/auth/components/AuthCard';
import { brandTextStyle } from '@/features/auth/components/authFieldStyles';
import { useAuth } from '@/features/auth/hooks/useAuth';

const schema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(8),
    confirmPassword: z.string().min(8)
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    message: 'passwordsMustMatch',
    path: ['confirmPassword']
  });

type FormValues = z.infer<typeof schema>;

export const ChangePasswordForm = () => {
  const { t } = useTranslation();
  const { changePassword, changePasswordMutation } = useAuth();

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: { currentPassword: '', newPassword: '', confirmPassword: '' }
  });

  const handleSubmit = (values: FormValues) => {
    changePassword(values.currentPassword, values.newPassword);
  };

  return (
    <AuthCard px={32} py={36} w={{ base: '100%', md: 440 }}>
      <Stack align="center" gap={14}>
        <Center
          w={56}
          h={56}
          style={{
            borderRadius: 12,
            backgroundColor: 'var(--color-accent-secondary-soft)',
            color: 'var(--color-icon-accent)'
          }}
        >
          <IconFigmaLock size={24} />
        </Center>
        <Title order={2} fz={24} fw={700} ta="center" style={brandTextStyle}>
          {t('changePassword.title')}
        </Title>
        <Text size="sm" ta="center" style={{ color: 'var(--color-text-secondary)' }}>
          {t('changePassword.subtitle')}
        </Text>

        {changePasswordMutation.isError && <FormErrorMessage message={t('changePassword.error')} />}

        <form onSubmit={form.onSubmit(handleSubmit)} style={{ width: '100%' }} noValidate>
          <Stack gap="md">
            <PasswordField
              label={t('changePassword.currentPassword')}
              required
              {...form.getInputProps('currentPassword')}
            />
            <PasswordField
              label={t('changePassword.newPassword')}
              required
              {...form.getInputProps('newPassword')}
            />
            <PasswordField
              label={t('changePassword.confirmPassword')}
              required
              error={
                form.errors.confirmPassword ? t('changePassword.passwordsMustMatch') : undefined
              }
              {...form.getInputProps('confirmPassword')}
            />

            <MutationButton
              type="submit"
              fullWidth
              isPending={changePasswordMutation.isPending}
              isPaused={changePasswordMutation.isPaused}
            >
              {t('changePassword.submit')}
            </MutationButton>
          </Stack>
        </form>
      </Stack>
    </AuthCard>
  );
};
